import React, { useState, useEffect, useCallback } from 'react';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Plus, Loader2, X } from 'lucide-react';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

export default function MfgOpeningStock() {
  const mfg = useMfgApi();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [units, setUnits] = useState([]);
  const [skus, setSkus] = useState([]);
  const { isOwner } = useMfgRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stock, u, s] = await Promise.all([mfg.get('/opening-stock'), mfg.get('/units'), mfg.get('/skus')]);
      setRows(stock.items || stock || []);
      setUnits((u.items || u || []).filter(x => x.is_active !== false));
      setSkus((s.items || s || []).filter(x => x.is_active !== false));
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="mfg-opening-stock-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Opening Stock<MfgReadOnlyBadge /></h1>
        {isOwner && <button data-testid="mfg-opening-stock-add" onClick={() => setShowNew(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-bold hover:bg-indigo-600 transition-colors"><Plus size={14} /> Set Opening Stock</button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="opening-stock-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <th className="text-left py-2.5 px-3">Unit</th><th className="text-left py-2.5 px-3">SKU</th>
              <th className="text-right py-2.5 px-3">Quantity (pcs)</th><th className="text-left py-2.5 px-3">As Of</th>
              <th className="text-left py-2.5 px-3">Notes</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={5} className="text-center py-12 text-gray-400">No opening stock set.</td></tr> : rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-gray-50">
                  <td className="py-2.5 px-3 font-medium">{r.unit_name || '—'}</td>
                  <td className="py-2.5 px-3">{r.sku_name || '—'}</td>
                  <td className="py-2.5 px-3 text-right font-bold">{r.quantity_pieces?.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-gray-500">{r.as_of_date || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-500">{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && isOwner && <NewOpeningStockModal units={units} skus={skus} mfg={mfg} toast={toast} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOpeningStockModal({ units, skus, mfg, toast, onClose, onCreated }) {
  const [form, setForm] = useState({ unit_id: '', sku_id: '', quantity_pieces: '', as_of_date: new Date().toISOString().split('T')[0], notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await mfg.post('/opening-stock', { unit_id: form.unit_id, sku_id: form.sku_id, quantity_pieces: Number(form.quantity_pieces), as_of_date: form.as_of_date, notes: form.notes || undefined });
      toast.show('Opening stock set', 'success'); onCreated();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  const valid = form.unit_id && form.sku_id && Number(form.quantity_pieces) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="opening-stock-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-extrabold text-gray-900">Set Opening Stock</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button></div>
        <div className="space-y-3">
          <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Unit</span><select data-testid="os-unit" className="input-sm mt-1" value={form.unit_id} onChange={e => set('unit_id', e.target.value)}><option value="">Select...</option>{units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">SKU</span><select data-testid="os-sku" className="input-sm mt-1" value={form.sku_id} onChange={e => set('sku_id', e.target.value)}><option value="">Select...</option>{skus.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
          <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Quantity (pcs)</span><input data-testid="os-qty" type="number" min="0" className="input-sm mt-1" value={form.quantity_pieces} onChange={e => set('quantity_pieces', e.target.value)} /></label>
          <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">As Of Date</span><input data-testid="os-date" type="date" className="input-sm mt-1" value={form.as_of_date} onChange={e => set('as_of_date', e.target.value)} /></label>
          {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
        </div>
        <button data-testid="os-submit" onClick={submit} disabled={saving || !valid} className="w-full mt-4 h-11 rounded-xl bg-indigo-500 text-white font-bold text-sm hover:bg-indigo-600 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Save'}
        </button>
      </div>
    </div>
  );
}
